import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { InstrumentTypeResponse } from "../codegen-api";
import {
  getTradableTradingTypes,
  getRouteSegmentForInstrument,
} from "../config/features";

/**
 * Sync a single-select "sub-tab"/filter choice with a URL query param so the
 * selection is deep-linkable, shareable, and survives reloads. The default
 * value is represented by the ABSENCE of the param (keeps URLs clean), and any
 * value not in `allowed` falls back to the default. History entries are
 * replaced (not pushed) so flipping filters doesn't pollute the back button.
 */
export function useUrlParamState<T extends string>(
  key: string,
  defaultValue: T,
  allowed: readonly T[]
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const value =
    raw !== null && (allowed as readonly string[]).includes(raw)
      ? (raw as T)
      : defaultValue;

  const setValue = useCallback(
    (next: T) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === defaultValue) {
            params.delete(key);
          } else {
            params.set(key, next);
          }
          return params;
        },
        { replace: true }
      );
    },
    [key, defaultValue, setSearchParams]
  );

  return [value, setValue];
}

/**
 * URL-backed tab selection for an enum whose values are DISPLAY strings (often
 * containing spaces, e.g. "Position History") and therefore unusable as URL
 * values directly.
 *
 * Pass a slug map — `{ [enumValue]: "kebab-slug" }` — and the tab becomes
 * deep-linkable as `?<key>=<slug>` while the component keeps working in enum
 * terms. Same conventions as `useUrlParamState`: the default tab is the ABSENCE
 * of the param, unknown slugs fall back to the default, and history is replaced
 * rather than pushed.
 *
 * NOTE on `key`: the instrument-type filter already owns `tab` (see
 * `useUrlInstrumentFilter`), so tab groups that can appear on the same screen
 * must use distinct keys — the trading page uses `panel` for the bottom bar.
 */
export function useUrlTabState<T extends string>(
  key: string,
  defaultValue: T,
  slugs: Record<T, string>
): [T, (value: T) => void] {
  const slugToTab = useMemo(() => {
    const out = {} as Record<string, T>;
    (Object.keys(slugs) as T[]).forEach((tab) => {
      out[slugs[tab]] = tab;
    });
    return out;
  }, [slugs]);

  const allowed = useMemo(
    () => Object.values(slugs) as string[],
    [slugs]
  );

  const [slug, setSlug] = useUrlParamState(
    key,
    slugs[defaultValue],
    allowed
  );

  const setTab = useCallback(
    (next: T) => setSlug(slugs[next]),
    [setSlug, slugs]
  );

  return [slugToTab[slug] ?? defaultValue, setTab];
}

const ALL_MARKETS_VALUE = "all";
type MarketFilterUrlValue = string;

/**
 * URL-backed variant of the instrument-type filter shared by the Positions,
 * Orders, Order History, Position History and Trade History portfolio sub-tabs.
 * `undefined` (= "all markets") maps to an absent param.
 *
 * `enabled` lets shared tabs opt out of URL syncing when embedded outside the
 * portfolio (e.g. the trading bottom bar), where the param would collide with
 * the trading page URL — those fall back to plain local state.
 */
export function useUrlInstrumentFilter(
  key = "tab",
  enabled = true
): [InstrumentTypeResponse | undefined, (filter?: InstrumentTypeResponse) => void] {
  // Allowed slugs come from the trading-type registry, so `?tab=spot` is only
  // honoured while spot is enabled (an unknown slug falls back to "all") and a
  // future vertical is supported without touching this hook.
  // Tradable, not merely enabled: a vertical with no instrument type could
  // otherwise contribute a slug that maps straight back to "all".
  const enabledTypes = getTradableTradingTypes();
  const allowedValues = [
    ALL_MARKETS_VALUE,
    ...enabledTypes.map((definition) => definition.routeSegment),
  ];

  const [urlValue, setUrlValue] = useUrlParamState<MarketFilterUrlValue>(
    key,
    ALL_MARKETS_VALUE,
    allowedValues
  );
  const [localValue, setLocalValue] = useState<InstrumentTypeResponse>();

  if (!enabled) {
    return [localValue, setLocalValue];
  }

  const instrumentType =
    enabledTypes.find((definition) => definition.routeSegment === urlValue)
      ?.instrumentType ?? undefined;

  const setInstrumentType = (filter?: InstrumentTypeResponse) => {
    setUrlValue(
      filter ? getRouteSegmentForInstrument(filter) : ALL_MARKETS_VALUE
    );
  };

  return [instrumentType, setInstrumentType];
}
